'use client';

import React from 'react';

import type { NormalizedCaptureRegion } from '@/shared/captureRegion';

type MediaDisplayCaptureMaskProps = {
  region: NormalizedCaptureRegion;
};

/**
 * Figma "Capture Mask": four brand rects at 25% opacity covering everything
 * outside the selection. The selected region stays clear.
 *
 * The fill is aliased once here because `@tranquility-ai/theme-core/theme.css`
 * is not loaded globally in this app, so a bare `var(--surface-brand)` resolves
 * to nothing and paints the mask transparent. The literal matches Blue/100 and
 * defers to the token wherever theme-core is in scope.
 */
export function MediaDisplayCaptureMask({ region }: MediaDisplayCaptureMaskProps) {
  const { x, y, width, height } = region;

  return (
    <div
      data-capture-overlay="true"
      data-testid="capture-selection-mask"
      className="pointer-events-none absolute inset-0 z-[10050]"
      aria-hidden="true"
      style={{ '--capture-mask-fill': 'var(--surface-brand, #0387fe)' } as React.CSSProperties}
    >
      <div
        data-testid="capture-mask-top"
        className="absolute bg-[var(--capture-mask-fill)] opacity-25"
        style={{ left: 0, top: 0, width: '100%', height: Math.max(0, y) }}
      />
      <div
        data-testid="capture-mask-left"
        className="absolute bg-[var(--capture-mask-fill)] opacity-25"
        style={{
          left: 0,
          top: y,
          width: Math.max(0, x),
          height: Math.max(0, height),
        }}
      />
      <div
        data-testid="capture-mask-right"
        className="absolute bg-[var(--capture-mask-fill)] opacity-25"
        style={{
          left: x + width,
          top: y,
          width: `calc(100% - ${x + width}px)`,
          height: Math.max(0, height),
        }}
      />
      <div
        data-testid="capture-mask-bottom"
        className="absolute bg-[var(--capture-mask-fill)] opacity-25"
        style={{ left: 0, top: y + height, width: '100%', bottom: 0 }}
      />
    </div>
  );
}
